import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { 
  X, ArrowRight, ArrowLeft, CheckCircle2, Sparkles,
  Building2, MapPin, Monitor, AlertTriangle, Target, User, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Business types
const BUSINESS_TYPES = [
  { id: 'restaurant', label: 'Restaurant' },
  { id: 'cafe', label: 'Café' },
  { id: 'bar', label: 'Bar' },
  { id: 'fast_food', label: 'Schnellrestaurant / Imbiss' },
  { id: 'franchise', label: 'Systemgastronomie / Franchise' },
  { id: 'catering', label: 'Event- oder Cateringbetrieb' },
  { id: 'hotel', label: 'Hotel mit Gastronomie' },
  { id: 'other', label: 'Anderes' }
];

// Location counts
const LOCATION_COUNTS = [
  { id: '1', label: '1 Standort' },
  { id: '2-3', label: '2-3 Standorte' },
  { id: '4-9', label: '4-9 Standorte' },
  { id: '10+', label: '10 oder mehr Standorte' }
];

// Systems from Gastro-Landschaft
const GASTRO_SYSTEMS = [
  { id: 'pos_payment', label: 'Kassensystem & Kartenzahlung' },
  { id: 'reservation', label: 'Reservierungssysteme' },
  { id: 'staff_planning', label: 'Personal- und Schichtplanung' },
  { id: 'smart_order', label: 'Smarte Order-Lösungen' },
  { id: 'delivery', label: 'Delivery-Integration' },
  { id: 'loyalty', label: 'Loyalty & Kundenbindung' },
  { id: 'optimization', label: 'Digitale Betriebsoptimierung' },
  { id: 'social_media', label: 'Social Media' },
  { id: 'website_reviews', label: 'Website- & Bewertungsmanagement' }
];

// Challenges
const CHALLENGES = [
  { id: 'too_many_systems', label: 'Zu viele verschiedene Systeme' },
  { id: 'bad_integration', label: 'Schlechte Integration zwischen Tools' },
  { id: 'training', label: 'Aufwändige Schulung der Mitarbeiter' },
  { id: 'no_support', label: 'Mangelnder Support / kein Ansprechpartner' },
  { id: 'no_overview', label: 'Fehlender Überblick über Verträge & Kosten' },
  { id: 'duplicate_data', label: 'Unklare Daten oder doppelte Eingaben' },
  { id: 'no_time', label: 'Zu wenig Zeit für technische Themen' },
  { id: 'other', label: 'Anderes' }
];

// Goals
const GOALS = [
  { id: 'new_systems', label: 'Neue Systemempfehlungen' },
  { id: 'better_support', label: 'Besseren Support für bestehende Tools' },
  { id: 'overview', label: 'Gesamtübersicht / System-Landschaft' },
  { id: 'onboarding', label: 'Unterstützung bei Einführung / Schulung' }
];

const TOTAL_STEPS = 7;

const ConsultationWizard = ({ isOpen, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [consultationId, setConsultationId] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [userCreated, setUserCreated] = useState(false);
  const [existingUserData, setExistingUserData] = useState(null); // User data from registration
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  // Check if user is pending approval (from registration)
  const isPendingUser = isAuthenticated && user?.status === 'pending_approval';
  
  // Check if we can skip contact step (user already provided data during registration)
  const canSkipContactStep = existingUserData?.has_complete_profile || (isAuthenticated && user?.name);
  
  // Form data
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  
  const [businessTypes, setBusinessTypes] = useState([]);
  const [otherBusinessType, setOtherBusinessType] = useState('');
  const [locationCount, setLocationCount] = useState('');
  const [currentSystems, setCurrentSystems] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [otherChallenge, setOtherChallenge] = useState('');
  const [mainGoal, setMainGoal] = useState('');
  
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [specialRequirements, setSpecialRequirements] = useState('');

  // Pre-fill if logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      setEmail(user.email);
      setEmailVerified(true);
      setContactName(user.name || '');
      setContactPhone(user.phone || '');
    }
  }, [isAuthenticated, user]);

  // Handle verification token from URL (when user clicks link in email)
  useEffect(() => {
    const verifyToken = searchParams.get('verify_token');
    const emailFromUrl = searchParams.get('email');
    
    if (verifyToken && emailFromUrl && isOpen) {
      // Auto-verify via token
      const verifyViaToken = async () => {
        setLoading(true);
        try {
          const response = await axios.post(`${API}/consultation/verify-token`, {
            email: emailFromUrl,
            token: verifyToken
          });
          
          setEmail(emailFromUrl);
          setEmailVerified(true);
          setConsultationId(response.data.consultation_id);
          
          if (response.data.existing_user) {
            setExistingUserData(response.data.existing_user);
            setContactName(response.data.existing_user.name || '');
            setContactPhone(response.data.existing_user.phone || '');
          }
          
          setCurrentStep(2);
          
          // Clear URL params
          setSearchParams({});
        } catch (error) {
          console.error('Token verification failed:', error);
          // If token failed, show the wizard normally
          setEmail(emailFromUrl);
          setPrivacyAccepted(true);
        } finally {
          setLoading(false);
        }
      };
      
      verifyViaToken();
    }
  }, [searchParams, isOpen, setSearchParams]);

  // Start consultation on first real step
  const startConsultation = async () => {
    if (consultationId) return consultationId;
    
    try {
      const response = await axios.post(`${API}/consultation/start`, {
        email: email,
        is_logged_in: isAuthenticated
      });
      setConsultationId(response.data.consultation_id);
      return response.data.consultation_id;
    } catch (error) {
      console.error('Failed to start consultation:', error);
      return null;
    }
  };

  // Save step data
  const saveStepData = async (stepName, data) => {
    const id = consultationId || await startConsultation();
    if (!id) return;
    
    try {
      await axios.post(`${API}/consultation/${id}/step`, {
        step_name: stepName,
        data: data
      });
    } catch (error) {
      console.error('Failed to save step:', error);
    }
  };

  // Send verification code
  const sendVerificationCode = async () => {
    if (!email || !privacyAccepted) return;
    
    setLoading(true);
    setResendSuccess(false);
    try {
      await axios.post(`${API}/consultation/send-code`, { email });
      setCodeSent(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Senden des Codes');
    } finally {
      setLoading(false);
    }
  };

  // Resend verification code
  const resendVerificationCode = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await axios.post(`${API}/consultation/send-code`, { email });
      setResendSuccess(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Senden des Codes');
    } finally {
      setResendLoading(false);
    }
  };

  // Verify code
  const verifyCode = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/consultation/verify-code`, { 
        email, 
        code: verificationCode 
      });
      setEmailVerified(true);
      setConsultationId(response.data.consultation_id);
      
      // Check if user already has complete profile (from registration)
      if (response.data.existing_user) {
        setExistingUserData(response.data.existing_user);
        // Pre-fill contact form with existing data
        setContactName(response.data.existing_user.name || '');
        setContactPhone(response.data.existing_user.phone || '');
      }
      
      setCurrentStep(2);
    } catch (error) {
      alert(error.response?.data?.detail || 'Ungültiger Code');
    } finally {
      setLoading(false);
    }
  };

  // Handle multi-select toggle
  const toggleSelection = (item, list, setList) => {
    if (list.includes(item)) {
      setList(list.filter(i => i !== item));
    } else {
      setList([...list, item]);
    }
  };

  // Submit final step
  const submitConsultation = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/consultation/${consultationId}/complete`, {
        contact_name: contactName,
        contact_phone: contactPhone,
        special_requirements: specialRequirements
      });
      setCompleted(true);
      setUserCreated(response.data.user_created || false);
    } catch (error) {
      alert('Fehler beim Absenden. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Navigation
  const canProceed = () => {
    switch (currentStep) {
      case 0: return true; // Intro
      case 1: return emailVerified || (isAuthenticated && user); // Email verification
      case 2: return businessTypes.length > 0; // Business type
      case 3: return locationCount !== ''; // Locations
      case 4: return true; // Current systems (optional)
      case 5: return challenges.length > 0; // Challenges
      case 6: return mainGoal !== ''; // Goal
      case 7: return contactName.trim() !== ''; // Contact
      default: return true;
    }
  };

  const nextStep = async () => {
    // Save data before moving to next step
    if (currentStep === 2 && businessTypes.length > 0) {
      await saveStepData('business_types', { 
        types: businessTypes, 
        other: otherBusinessType 
      });
    } else if (currentStep === 3 && locationCount) {
      await saveStepData('location_count', { count: locationCount });
    } else if (currentStep === 4) {
      await saveStepData('current_systems', { systems: currentSystems });
    } else if (currentStep === 5 && challenges.length > 0) {
      await saveStepData('challenges', { 
        challenges: challenges, 
        other: otherChallenge 
      });
    } else if (currentStep === 6 && mainGoal) {
      await saveStepData('main_goal', { goal: mainGoal });
    }
    
    // Skip email step if logged in
    if (currentStep === 0 && isAuthenticated) {
      await startConsultation();
      setCurrentStep(2);
    } else if (currentStep === 1 && emailVerified) {
      setCurrentStep(2);
    } else if (currentStep === 6 && canSkipContactStep) {
      // Skip contact details step if user already has complete profile
      // Use existing data for submission
      await saveStepData('main_goal', { goal: mainGoal });
      
      // Set contact data from existing user
      if (existingUserData) {
        setContactName(existingUserData.name);
        setContactPhone(existingUserData.phone || '');
      } else if (isAuthenticated && user) {
        setContactName(user.name);
        setContactPhone(user.phone || '');
      }
      
      await submitConsultation();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep === 2 && isAuthenticated) {
      setCurrentStep(0);
    } else {
      setCurrentStep(prev => Math.max(0, prev - 1));
    }
  };

  // Progress calculation - for logged in users or users with complete profile: skip email (step 1) and contact (step 7)
  const getProgress = () => {
    if (isAuthenticated || canSkipContactStep) {
      // Steps for logged in or existing users: 0 (intro), 2-6 (business, locations, systems, challenges, goals) = 6 steps total
      const adjustedStep = currentStep <= 1 ? 0 : currentStep - 1;
      return (adjustedStep / 5) * 100; // 5 steps for these users
    }
    return (currentStep / TOTAL_STEPS) * 100;
  };

  // Total steps for display
  const getTotalSteps = () => (isAuthenticated || canSkipContactStep) ? 6 : TOTAL_STEPS;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-ink/15">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-ink">Digitale Erstberatung</h2>
            <button onClick={onClose} className="text-ink/60 hover:text-ink/80">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Progress bar */}
          {!completed && currentStep > 0 && (
            <div className="w-full bg-off rounded-none h-2">
              <div 
                className="bg-purple h-2 rounded-none transition-all duration-300"
                style={{ width: `${getProgress()}%` }}
              />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {completed ? (
            /* Completion Screen */
            <div className="text-center py-8">
              <div className="w-20 h-20 rounded-none bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-ink mb-4">
                Vielen Dank für deine Anfrage!
              </h3>
              
              {userCreated ? (
                /* New user created via consultation */
                <>
                  <p className="text-ink/80 mb-4 max-w-md mx-auto">
                    Wir haben deine Angaben erhalten und einen Account für dich angelegt.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-none p-4 mb-6 max-w-md mx-auto">
                    <p className="text-amber-800 font-medium mb-2">Nächster Schritt:</p>
                    <p className="text-amber-700 text-lg">
                      Dein Account wird derzeit geprüft. Wir melden uns in Kürze bei dir und schalten deinen Zugang frei.
                    </p>
                  </div>
                  <p className="text-ink/60 text-lg mb-6">
                    Eine Bestätigung wurde an {email} gesendet.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button onClick={onClose} variant="outline" className="text-purple">
                      Schließen
                    </Button>
                    <Link to="/registrierung-geprueft" onClick={onClose}>
                      <Button className="">
                        Zur Übersicht
                      </Button>
                    </Link>
                  </div>
                </>
              ) : (
                /* Existing user or logged-in user */
                <>
                  <p className="text-ink/80 mb-6 max-w-md mx-auto">
                    Wir haben deine Angaben erhalten und werden uns innerhalb von 24 Stunden bei dir melden. 
                    Eine Bestätigung wurde an {email} gesendet.
                  </p>
                  <Button onClick={onClose} className="">
                    Schließen
                  </Button>
                </>
              )}
            </div>
          ) : currentStep === 0 ? (
            /* Step 0: Intro */
            <div className="text-center">
              <div className="w-20 h-20 rounded-none bg-purple/10 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-10 h-10 text-purple" />
              </div>
              <h3 className="text-2xl font-bold text-ink mb-4">
                Finde in 2 Minuten heraus, welche digitalen Tools zu deinem Betrieb passen.
              </h3>
              <p className="text-ink/80 mb-8 max-w-lg mx-auto">
                Beantworte ein paar kurze Fragen – wir empfehlen dir anschließend die beste 
                Kombination zum Beispiel aus Kassensystem, Reservierung und Personalplanung.
              </p>
              <Button 
                onClick={nextStep}
                className="px-8 text-lg"
              >
                Jetzt Beratung starten
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          ) : currentStep === 1 && !isAuthenticated ? (
            /* Step 1: Email Verification (only if not logged in) */
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-none bg-purple/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-purple" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-ink">E-Mail-Adresse bestätigen</h3>
                  <p className="text-ink/60 text-lg">Damit wir deine Ergebnisse speichern können</p>
                </div>
              </div>

              {!codeSent ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-lg font-medium text-ink/80 mb-2">E-Mail-Adresse</label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ihre@email.de"
                      className="w-full"
                    />
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="privacy"
                      checked={privacyAccepted}
                      onCheckedChange={setPrivacyAccepted}
                    />
                    <label htmlFor="privacy" className="text-lg text-ink/80">
                      Ich bin einverstanden, dass gastro-beratung.de meine Angaben zur Bearbeitung 
                      meiner Anfrage verarbeitet. Weitere Infos in der{' '}
                      <Link to="/datenschutz" className="text-purple underline" target="_blank">
                        Datenschutzerklärung
                      </Link>.
                    </label>
                  </div>

                  <Button
                    onClick={sendVerificationCode}
                    disabled={!email || !privacyAccepted || loading}
                    className="w-full"
                  >
                    {loading ? 'Wird gesendet...' : 'Code senden'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-ink/80">
                    Wir haben einen Bestätigungscode an <strong>{email}</strong> gesendet.
                  </p>
                  <p className="text-ink/60 text-lg">
                    Gib den Code ein oder klicke auf den Link in der E-Mail.
                  </p>
                  <div>
                    <label className="block text-lg font-medium text-ink/80 mb-2">Bestätigungscode</label>
                    <Input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="123456"
                      maxLength={6}
                      className="w-full text-center text-2xl tracking-widest"
                    />
                  </div>
                  <Button
                    onClick={verifyCode}
                    disabled={verificationCode.length < 6 || loading}
                    className="w-full"
                  >
                    {loading ? 'Wird überprüft...' : 'Code bestätigen'}
                  </Button>
                  
                  {/* Resend code section */}
                  <div className="pt-2 border-t border-ink/15">
                    {resendSuccess ? (
                      <p className="text-green-600 text-center text-lg">
                        ✓ Ein neuer Code wurde gesendet!
                      </p>
                    ) : (
                      <button 
                        onClick={resendVerificationCode}
                        disabled={resendLoading}
                        className="text-lg text-purple hover:underline w-full text-center flex items-center justify-center gap-2"
                      >
                        {resendLoading ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Wird gesendet...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4" />
                            Code erneut senden
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  
                  <button 
                    onClick={() => { setCodeSent(false); setVerificationCode(''); setResendSuccess(false); }}
                    className="text-lg text-ink/60 hover:underline w-full text-center"
                  >
                    Andere E-Mail-Adresse verwenden
                  </button>
                </div>
              )}
            </div>
          ) : currentStep === 2 ? (
            /* Step 2: Business Type */
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-none bg-purple/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-purple" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-ink">Welche Art von Betrieb führst du?</h3>
                  <p className="text-ink/60 text-lg">Mehrfachauswahl möglich</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {BUSINESS_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => toggleSelection(type.id, businessTypes, setBusinessTypes)}
                    className={`p-4 rounded-none border text-left transition-all ${
                      businessTypes.includes(type.id)
                        ? 'border-purple bg-purple/5 ring-2 ring-purple'
                        : 'border-ink/15 hover:border-ink/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-ink">{type.label}</span>
                      {businessTypes.includes(type.id) && (
                        <CheckCircle2 className="w-5 h-5 text-purple" />
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {businessTypes.includes('other') && (
                <div className="mt-4">
                  <Input
                    value={otherBusinessType}
                    onChange={(e) => setOtherBusinessType(e.target.value)}
                    placeholder="Bitte beschreibe deinen Betrieb..."
                    className="w-full"
                  />
                </div>
              )}
            </div>
          ) : currentStep === 3 ? (
            /* Step 3: Location Count */
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-none bg-purple/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-purple" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-ink">Wie viele Standorte betreibst du aktuell?</h3>
                  <p className="text-ink/60 text-lg">Dies hilft uns zu verstehen, ob zentrales Management sinnvoll ist.</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {LOCATION_COUNTS.map((count) => (
                  <button
                    key={count.id}
                    onClick={() => setLocationCount(count.id)}
                    className={`p-4 rounded-none border text-center transition-all ${
                      locationCount === count.id
                        ? 'border-purple bg-purple/5 ring-2 ring-purple'
                        : 'border-ink/15 hover:border-ink/30'
                    }`}
                  >
                    <span className="font-medium text-ink">{count.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : currentStep === 4 ? (
            /* Step 4: Current Systems */
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-none bg-purple/10 flex items-center justify-center">
                  <Monitor className="w-5 h-5 text-purple" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-ink">Welche digitalen Systeme nutzt du aktuell?</h3>
                  <p className="text-ink/60 text-lg">Mehrfachauswahl möglich – wähle alle zutreffenden Systeme.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {GASTRO_SYSTEMS.map((system) => (
                  <button
                    key={system.id}
                    onClick={() => toggleSelection(system.id, currentSystems, setCurrentSystems)}
                    className={`p-3 rounded-none border text-left transition-all ${
                      currentSystems.includes(system.id)
                        ? 'border-purple bg-purple/5 ring-2 ring-purple'
                        : 'border-ink/15 hover:border-ink/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-medium text-ink">{system.label}</span>
                      {currentSystems.includes(system.id) && (
                        <CheckCircle2 className="w-4 h-4 text-purple" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
              
              <p className="text-ink/60 text-lg mt-4 text-center">
                Du kannst diesen Schritt überspringen, wenn du noch keine Systeme nutzt.
              </p>
            </div>
          ) : currentStep === 5 ? (
            /* Step 5: Challenges */
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-none bg-purple/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-purple" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-ink">Was sind aktuell deine größten Herausforderungen?</h3>
                  <p className="text-ink/60 text-lg">Mehrfachauswahl möglich – was beschreibt deine Situation am besten?</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {CHALLENGES.map((challenge) => (
                  <button
                    key={challenge.id}
                    onClick={() => toggleSelection(challenge.id, challenges, setChallenges)}
                    className={`p-4 rounded-none border text-left transition-all ${
                      challenges.includes(challenge.id)
                        ? 'border-purple bg-purple/5 ring-2 ring-purple'
                        : 'border-ink/15 hover:border-ink/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-ink">{challenge.label}</span>
                      {challenges.includes(challenge.id) && (
                        <CheckCircle2 className="w-5 h-5 text-purple" />
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {challenges.includes('other') && (
                <div className="mt-4">
                  <Input
                    value={otherChallenge}
                    onChange={(e) => setOtherChallenge(e.target.value)}
                    placeholder="Bitte beschreibe deine Herausforderung..."
                    className="w-full"
                  />
                </div>
              )}
            </div>
          ) : currentStep === 6 ? (
            /* Step 6: Main Goal */
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-none bg-purple/10 flex items-center justify-center">
                  <Target className="w-5 h-5 text-purple" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-ink">Was wünschst du dir aktuell am meisten?</h3>
                  <p className="text-ink/60 text-lg">Dein Hauptziel für die Beratung – wir richten uns danach.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {GOALS.map((goal) => (
                  <button
                    key={goal.id}
                    onClick={() => setMainGoal(goal.id)}
                    className={`p-4 rounded-none border text-left transition-all ${
                      mainGoal === goal.id
                        ? 'border-purple bg-purple/5 ring-2 ring-purple'
                        : 'border-ink/15 hover:border-ink/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-ink">{goal.label}</span>
                      {mainGoal === goal.id && (
                        <CheckCircle2 className="w-5 h-5 text-purple" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : currentStep === 7 ? (
            /* Step 7: Contact Details */
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-none bg-purple/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-purple" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-ink">Deine persönliche Empfehlung ist fast fertig.</h3>
                  <p className="text-ink/60 text-lg">Gib kurz deine Kontaktdaten an, damit wir dir die Ergebnisse zusenden können.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-lg font-medium text-ink/80 mb-2">Name *</label>
                  <Input
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Dein Name"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-lg font-medium text-ink/80 mb-2">E-Mail</label>
                  <Input
                    value={email}
                    disabled
                    className="w-full bg-off"
                  />
                </div>
                
                <div>
                  <label className="block text-lg font-medium text-ink/80 mb-2">Telefonnummer (optional)</label>
                  <Input
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder="+49 123 456789"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-lg font-medium text-ink/80 mb-2">
                    Hast du spezielle Anforderungen von denen du berichten möchtest?
                  </label>
                  <Textarea
                    value={specialRequirements}
                    onChange={(e) => setSpecialRequirements(e.target.value)}
                    placeholder="Optionale Anmerkungen..."
                    rows={3}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer with navigation */}
        {!completed && currentStep > 0 && (
          <div className="p-6 border-t border-ink/15 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={prevStep}
              disabled={currentStep === 0 || (currentStep === 1 && !isAuthenticated && codeSent)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            
            {currentStep === 7 ? (
              <Button
                onClick={submitConsultation}
                disabled={!canProceed() || loading}
                className=""
              >
                {loading ? 'Wird gesendet...' : 'Ergebnisse anfordern'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : currentStep !== 1 ? (
              <Button
                onClick={nextStep}
                disabled={!canProceed()}
                className=""
              >
                Weiter
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsultationWizard;
